import { NAME_MAX, TEXT_MAX } from './client.ts';
import { normalizeEmail, normalizePhone, normalizeText } from './normalize.ts';

/** Каналы связи контакта. */
export const CHANNEL_KINDS = ['phone', 'email', 'whatsapp', 'max', 'vk', 'telegram', 'other'] as const;
export type ChannelKind = (typeof CHANNEL_KINDS)[number];

/** У контакта — до 20 способов связи: больше — явная ошибка ввода. */
export const CHANNELS_MAX = 20;

export interface ChannelInput {
  readonly kind: ChannelKind;
  readonly value: string;
}

/** Способ связи как храним: `value` — как ввели, `normalized` — для сравнения и поиска дублей. */
export interface Channel {
  readonly kind: ChannelKind;
  readonly value: string;
  readonly normalized: string;
}

/** Ссылки и «@» у мессенджеров и соцсетей отбрасываются: `https://t.me/Anna` и `@anna` — один адрес. */
function handle(value: string): string {
  return value
    .replace(/^https?:\/\/(?:www\.)?(?:t\.me|telegram\.me|vk\.com|vk\.ru|max\.ru)\//i, '')
    .replace(/^@/, '')
    .replace(/\/+$/, '')
    .toLowerCase();
}

/** Значение канала для сравнения; null — телефон или почта не разобрать, значение пустое. */
export function normalizeChannel(kind: ChannelKind, value: string): string | null {
  const text = normalizeText(value);
  if (text === null) return null;
  switch (kind) {
    case 'phone':
      return normalizePhone(text);
    case 'email':
      return normalizeEmail(text);
    case 'whatsapp':
    case 'max':
      // Эти мессенджеры живут на номере телефона; ник или ссылка тоже сохраняются.
      return normalizePhone(text) ?? handle(text);
    case 'telegram':
    case 'vk':
    case 'other':
      return handle(text);
  }
}

export interface ContactInput {
  readonly fullName: string;
  readonly position?: string | null | undefined;
  readonly channels: readonly ChannelInput[];
}

export interface ContactData {
  readonly fullName: string;
  readonly position: string | null;
  readonly channels: Channel[];
}

export type ContactIssue =
  | { readonly code: 'full_name_required' }
  | { readonly code: 'full_name_too_long' }
  | { readonly code: 'position_too_long' }
  | { readonly code: 'too_many_channels' }
  | { readonly code: 'channel_invalid'; readonly index: number; readonly kind: ChannelKind };

/** Каналы без пустых и повторов (по виду и значению для сравнения); неразобранные остаются — их покажет `contactIssues`. */
export function normalizeChannels(channels: readonly ChannelInput[]): {
  readonly channels: Channel[];
  readonly invalid: readonly { readonly index: number; readonly kind: ChannelKind }[];
} {
  const seen = new Set<string>();
  const out: Channel[] = [];
  const invalid: { index: number; kind: ChannelKind }[] = [];
  channels.forEach((channel, index) => {
    const value = normalizeText(channel.value);
    if (value === null) return;
    const normalized = normalizeChannel(channel.kind, value);
    if (normalized === null) {
      invalid.push({ index, kind: channel.kind });
      return;
    }
    const key = `${channel.kind}:${normalized}`;
    if (seen.has(key)) return;
    seen.add(key);
    out.push({ kind: channel.kind, value, normalized });
  });
  return { channels: out, invalid };
}

/** Контакт как храним и что в нём не так: пустой список проблем — можно сохранять. */
export function normalizeContact(input: ContactInput): { data: ContactData; issues: ContactIssue[] } {
  const fullName = normalizeText(input.fullName) ?? '';
  const position = normalizeText(input.position);
  const { channels, invalid } = normalizeChannels(input.channels);
  const issues: ContactIssue[] = [];
  if (fullName === '') issues.push({ code: 'full_name_required' });
  else if (fullName.length > NAME_MAX) issues.push({ code: 'full_name_too_long' });
  if (position !== null && position.length > TEXT_MAX) issues.push({ code: 'position_too_long' });
  if (input.channels.length > CHANNELS_MAX) issues.push({ code: 'too_many_channels' });
  for (const bad of invalid) issues.push({ code: 'channel_invalid', ...bad });
  return { data: { fullName, position, channels }, issues };
}

/** Есть ли способ связи, по которому можно найти дубль или написать: телефон или почта. */
export const hasPhoneOrEmail = (channels: readonly Channel[]): boolean =>
  channels.some((channel) => channel.kind === 'phone' || channel.kind === 'email');

/** Ссылки действий у канала: «Позвонить» (`tel:`) у телефона; почта пишется окном письма. */
export function channelHref(channel: Pick<Channel, 'kind' | 'normalized'>): string | null {
  if (channel.kind === 'phone') return `tel:${channel.normalized}`;
  if (channel.kind === 'telegram') return `https://t.me/${channel.normalized}`;
  return null;
}
